import React from 'react';
import { Button, Result } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import zhCN from '../../locales/zh-CN';
import enUS from '../../locales/en-US';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

const isChunkLoadError = (error?: Error) => {
  if (!error) return false;
  const msg = error.message || '';
  return msg.includes('ChunkLoadError') || msg.includes('Loading chunk') || msg.includes('Loading CSS chunk');
};

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, errorInfo);

    // 如果是 chunk 加载失败，自动刷新一次（只刷新一次，避免死循环）
    if (isChunkLoadError(error) && !window.sessionStorage.getItem('_chunk_reload')) {
      window.sessionStorage.setItem('_chunk_reload', '1');
      window.location.reload();
    }
  }

  handleReload = () => {
    window.sessionStorage.removeItem('_chunk_reload');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const chunkError = isChunkLoadError(this.state.error);
      const locale = localStorage.getItem('jdsuit_locale') || 'en';
      const messages = (locale === 'zh' ? zhCN : enUS) as Record<string, string>;
      const t = (key: string) => messages[key] || key;
      return (
        <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Result
            status="warning"
            title={chunkError ? t('页面资源已更新') : t('页面出现错误')}
            subTitle={
              chunkError
                ? t('检测到新版本，请点击下方按钮刷新页面获取最新资源。')
                : t('请尝试刷新页面，或联系管理员处理。')
            }
            extra={
              <Button type="primary" icon={<ReloadOutlined />} onClick={this.handleReload}>
                {t('刷新页面')}
              </Button>
            }
          />
        </div>
      );
    }
    return this.props.children;
  }
}
